import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetRef,
  Brand,
  BrandInput,
  InviteUserInput,
  Model,
  ModelInput,
  Variant,
  VariantInput,
} from '@motorcity/shared';
import { request } from './client';

export interface PublishRecord {
  publishId: string;
  status: 'pending' | 'success' | 'failed';
  startedAt: string;
  finishedAt?: string;
  requestedBy: string;
  note: string;
  rollbackOf?: string;
  snapshotKey?: string;
  counts?: { brands: number; models: number; variants: number; files: number };
  error?: string;
}

export interface CmsUser {
  username: string;
  email: string;
  name: string;
  enabled: boolean;
  status: string;
  createdAt?: string;
  roles: string[];
}

export const keys = {
  brands: ['brands'] as const,
  brand: (brandId: string) => ['brands', brandId] as const,
  tree: (brandId: string) => ['brands', brandId, 'tree'] as const,
  models: (brandId: string) => ['brands', brandId, 'models'] as const,
  model: (brandId: string, modelId: string) => ['brands', brandId, 'models', modelId] as const,
  variants: (brandId: string, modelId: string) =>
    ['brands', brandId, 'models', modelId, 'variants'] as const,
  variant: (brandId: string, modelId: string, variantId: string) =>
    ['brands', brandId, 'models', modelId, 'variants', variantId] as const,
  publish: ['publish'] as const,
  users: ['users'] as const,
};

// ---------------------------------------------------------------- brands

export interface BrandCounts {
  models: number;
  liveModels: number;
  variants: number;
}

/** The index ships counts alongside the brands, so the grid needs one request. */
export const useBrands = () =>
  useQuery({
    queryKey: keys.brands,
    queryFn: () =>
      request<{ brands: Brand[]; counts: Record<string, BrandCounts> }>('/brands'),
  });

export const useBrandTree = (brandId: string) =>
  useQuery({
    queryKey: keys.tree(brandId),
    queryFn: () =>
      request<{ brand: Brand; models: Model[]; variants: Variant[] }>(`/brands/${brandId}/tree`),
    enabled: Boolean(brandId),
  });

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BrandInput & { brandId: string }) =>
      request<{ brand: Brand }>('/brands', { method: 'POST', body }).then((r) => r.brand),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.brands }),
  });
}

export function useSaveBrand(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BrandInput) =>
      request<{ brand: Brand }>(`/brands/${brandId}`, { method: 'PUT', body }).then((r) => r.brand),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.brands });
      void qc.invalidateQueries({ queryKey: keys.tree(brandId) });
    },
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) => request(`/brands/${brandId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.brands }),
  });
}

export function useReorderBrands() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      request<{ brands: Brand[] }>('/brands/reorder', { method: 'POST', body: { ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.brands }),
  });
}

// ---------------------------------------------------------------- models

export function useCreateModel(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ModelInput & { modelId: string }) =>
      request<{ model: Model }>(`/brands/${brandId}/models`, { method: 'POST', body }).then(
        (r) => r.model,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

export function useSaveModel(brandId: string, modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ModelInput) =>
      request<{ model: Model }>(`/brands/${brandId}/models/${modelId}`, {
        method: 'PUT',
        body,
      }).then((r) => r.model),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

export function useDeleteModel(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modelId: string) =>
      request(`/brands/${brandId}/models/${modelId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

export function useReorderModels(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      request(`/brands/${brandId}/models/reorder`, { method: 'POST', body: { ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

// -------------------------------------------------------------- variants

export function useCreateVariant(brandId: string, modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VariantInput & { variantId: string }) =>
      request<{ variant: Variant }>(`/brands/${brandId}/models/${modelId}/variants`, {
        method: 'POST',
        body,
      }).then((r) => r.variant),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

export function useSaveVariant(brandId: string, modelId: string, variantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VariantInput) =>
      request<{ variant: Variant }>(
        `/brands/${brandId}/models/${modelId}/variants/${variantId}`,
        { method: 'PUT', body },
      ).then((r) => r.variant),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

export function useDeleteVariant(brandId: string, modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) =>
      request(`/brands/${brandId}/models/${modelId}/variants/${variantId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

export function useReorderVariants(brandId: string, modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      request(`/brands/${brandId}/models/${modelId}/variants/reorder`, {
        method: 'POST',
        body: { ids },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(brandId) }),
  });
}

// --------------------------------------------------------------- publish

export function usePublishHistory() {
  return useQuery({
    queryKey: keys.publish,
    queryFn: () => request<{ history: PublishRecord[] }>('/publish').then((r) => r.history),
    // Publishing is asynchronous, so poll while anything is still running.
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === 'pending') ? 3000 : false,
  });
}

export function usePublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => request('/publish', { method: 'POST', body: { note } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.publish }),
  });
}

export function useRollback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publishId: string) =>
      request(`/publish/${publishId}/rollback`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.publish }),
  });
}

// ----------------------------------------------------------------- users

export const useUsers = () =>
  useQuery({
    queryKey: keys.users,
    queryFn: () => request<{ users: CmsUser[] }>('/users').then((r) => r.users),
  });

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserInput) => request('/users', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, role }: { username: string; role: 'admin' | 'editor' }) =>
      request(`/users/${encodeURIComponent(username)}/role`, { method: 'PUT', body: { role } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useSetUserEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, enabled }: { username: string; enabled: boolean }) =>
      request(`/users/${encodeURIComponent(username)}/${enabled ? 'enable' : 'disable'}`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) =>
      request(`/users/${encodeURIComponent(username)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

// ---------------------------------------------------------------- assets

export function useFinalizeAsset() {
  return useMutation({
    mutationFn: (input: { key: string; contentType: string }) =>
      request<{ asset: AssetRef }>('/assets/finalize', { method: 'POST', body: input }).then(
        (r) => r.asset,
      ),
  });
}
